/** Bootstrap: wires uploader → pipeline → dashboard, with a top-level error boundary. */

import './styles.css';
import { CsvError, parseTransactions } from './lib/csv/transactions';
import { analyze } from './lib/detect/analyze';
import { savingsFor } from './model/savings';
import { Store, toLoadedFile } from './model/store';
import { UsageStore } from './model/usage';
import { renderTrend, renderCategories } from './ui/charts';
import { el } from './ui/dom';
import { Formatters } from './ui/format';
import { renderHero } from './ui/hero';
import { renderSavingsPanel } from './ui/savingsPanel';
import { renderSubscriptions } from './ui/subscriptions';
import { initUploader } from './ui/uploader';

const store = new Store();
const usage = new UsageStore();

const $ = <T extends HTMLElement = HTMLElement>(sel: string): T => {
  const node = document.querySelector<T>(sel);
  if (!node) throw new Error(`Missing element: ${sel}`);
  return node;
};

function showError(msg: string): void {
  const errEl = $('#drop-error');
  errEl.textContent = msg;
  errEl.hidden = false;
}

function loadText(text: string, fileName: string): void {
  try {
    const { transactions, warnings, currency } = parseTransactions(text);
    const result = analyze(transactions, currency);
    store.setLoaded(toLoadedFile(result, transactions, fileName, warnings, currency));
  } catch (e) {
    if (e instanceof CsvError) showError(e.message);
    else {
      console.error(e);
      showError('Something went wrong while analyzing this file. It may not be a transactions CSV.');
    }
  }
}

function render(): void {
  const loaded = store.loaded;
  $('#uploader').hidden = !!loaded;
  $('#dashboard').hidden = !loaded;
  $('#reset-btn').hidden = !loaded;
  if (!loaded) return;

  const fmt = new Formatters(loaded.currency);
  const { subscriptions, totals, monthlySeries } = loaded.result;
  const active = subscriptions.filter((s) => s.status === 'active');
  const savings = active.reduce((a, s) => a + savingsFor(s, usage), 0);

  const [from, to] = loaded.dateRange;
  $('#meta').replaceChildren(
    el('strong', {}, loaded.fileName),
    ` · ${loaded.txnCount} spend transactions · ${fmt.date(from)} – ${fmt.date(to)}`,
    ...(loaded.warnings.length ? [' · ', el('span', { class: 'warn' }, loaded.warnings.join(' '))] : []),
  );

  renderHero($('#hero'), totals, savings, fmt);
  renderSavingsPanel($('#savings-list'), active, usage, fmt);
  renderTrend($('#chart-trend'), monthlySeries);
  renderCategories($('#chart-cats'), active, fmt);
  renderSubscriptions($('#sub-list'), subscriptions, usage, fmt, (key, mark) => {
    usage.set(key, mark);
    store.touch();
  });
}

function init(): void {
  store.subscribe(render);

  initUploader({ onText: loadText, onError: showError });

  $('#reset-btn').addEventListener('click', () => {
    $('#drop-error').hidden = true;
    store.setLoaded(null);
  });

  $('#clear-marks-btn').addEventListener('click', () => {
    usage.clear();
    store.touch();
  });

  // Last-resort boundary: never leave the user staring at a half-rendered page.
  window.addEventListener('error', (e) => {
    console.error(e.error);
    showError('Unexpected error — reload the page and try again.');
  });

  render();
}

init();
